import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { marked } from "marked";
import { useTranslation } from "react-i18next";
import {
  Forward,
  Heart,
  MapPin,
  MessageCircle,
  MoreVertical,
  Pin,
} from "lucide-react";
import API_BASE, {
  dismissCurrentAnnouncement,
  getCurrentAnnouncement,
  withApiCredentials,
} from "../api";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import { fetchAnnouncementMarkdownWithFallback } from "../utils/announcementContent";
import {
  isVideoPostMedia,
  resolvePostMediaSrc,
} from "../utils/postMedia";
import { buildShareCountLookup, getShareCountForPost } from "../utils/postShares";
import { trackProductEvent } from "../analytics/trackEvent";
import useOrdinaryPostLikeMutation from "../hooks/useOrdinaryPostLikeMutation";
import TranslatablePostCaption from "./post/TranslatablePostCaption";
import PaginationSentinel from "./common/PaginationSentinel";
import ScrollToTopButton from "./scroll-to-top/ScrollToTopButton";
import useScrollToTopButton from "./scroll-to-top/useScrollToTopButton";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";
const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";
const DEBUG_FEED_VIDEO = true;
const ZOOM_VIDEO_BACKDROP_CLOSE_GUARD_MS = 650;
const CREATE_POST_HERO_IMAGE = 'url("/banner/Create_post.png")';
const CREATE_POST_HERO_IMAGE_DARK =
  'url("/banner/Darkmode/create-post-hero-dark.png")';
const FEED_VIDEO_POSTER_CANDIDATES = [
  ["poster"],
  ["posterUrl"],
  ["poster_url"],
  ["thumbnail"],
  ["thumbnailUrl"],
  ["thumbnail_url"],
  ["previewUrl"],
  ["preview_url"],
  ["imageUrl"],
  ["image_url"],
  ["coverUrl"],
  ["cover_url"],
  ["media", "poster"],
  ["media", "posterUrl"],
  ["media", "poster_url"],
  ["media", "thumbnail"],
  ["media", "thumbnailUrl"],
  ["media", "thumbnail_url"],
  ["media", "previewUrl"],
  ["media", "preview_url"],
  ["media", "imageUrl"],
  ["media", "image_url"],
  ["media", "coverUrl"],
  ["media", "cover_url"],
  ["videoMetadata", "poster"],
  ["videoMetadata", "posterUrl"],
  ["videoMetadata", "poster_url"],
  ["videoMetadata", "thumbnail"],
  ["videoMetadata", "thumbnailUrl"],
  ["videoMetadata", "thumbnail_url"],
  ["videoMetadata", "previewUrl"],
  ["videoMetadata", "preview_url"],
  ["videoMetadata", "coverUrl"],
  ["videoMetadata", "cover_url"],
];

function shortenFeedVideoUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  try {
    const baseUrl =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(rawValue, baseUrl);
    const pathParts = parsed.pathname.split("/").filter(Boolean);
    const shortPath = pathParts.slice(-3).join("/");
    return shortPath || parsed.pathname || parsed.hostname;
  } catch {
    const withoutQuery = rawValue.split(/[?#]/)[0];
    return withoutQuery.length > 96
      ? `...${withoutQuery.slice(-96)}`
      : withoutQuery;
  }
}

function getFeedVideoDebugSnapshot(video) {
  if (!video) {
    return { hasVideo: false };
  }

  const error = video.error
    ? {
        code: video.error.code,
        message: video.error.message || "",
      }
    : null;

  return {
    hasVideo: true,
    src: shortenFeedVideoUrl(video.currentSrc || video.src),
    readyState: video.readyState,
    networkState: video.networkState,
    paused: video.paused,
    currentTime: Number.isFinite(video.currentTime)
      ? Number(video.currentTime.toFixed(3))
      : video.currentTime,
    error,
  };
}

function getFeedVideoEventPreviewVideo(event) {
  const currentTarget = event?.currentTarget;
  if (!currentTarget) return null;
  if (currentTarget.tagName === "VIDEO") return currentTarget;
  if (typeof currentTarget.querySelector === "function") {
    return currentTarget.querySelector(".rp-post__media--video");
  }
  return null;
}

function debugFeedVideo(message, data = {}) {
  if (!DEBUG_FEED_VIDEO || typeof console === "undefined") return;
  console.debug("[FeedVideoDebug]", message, data);
}

function getNestedFeedVideoValue(source, path) {
  return path.reduce((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return current[key];
  }, source);
}

function resolveFeedVideoPosterUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^(https?:|data:|blob:)/i.test(rawValue)) return rawValue;
  return `${API_BASE}${rawValue}`;
}

function resolveFeedVideoPoster(post) {
  for (const path of FEED_VIDEO_POSTER_CANDIDATES) {
    const value = getNestedFeedVideoValue(post, path);
    const src = resolveFeedVideoPosterUrl(value);
    if (src) {
      return {
        src,
        field: path.join("."),
      };
    }
  }

  return {
    src: "",
    field: "",
  };
}

function normalizeTextValue(value) {
  return String(value || "").trim();
}

function hasMeaningfulValue(value) {
  const normalized = normalizeTextValue(value);
  return Boolean(normalized) && normalized !== "-";
}

function humanizeFeedValue(value) {
  return normalizeTextValue(value)
    .replace(/^[^.]+\./, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function translateFeedValue(t, value) {
  const normalized = normalizeTextValue(value);
  if (!normalized) return "";
  return t(normalized, { defaultValue: humanizeFeedValue(normalized) });
}

function looksLikeCoordinateLabel(value) {
  return /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(
    normalizeTextValue(value)
  );
}

function pickLocationLabel(...values) {
  for (const value of values) {
    const normalized = normalizeTextValue(value);
    if (
      normalized &&
      normalized !== "-" &&
      normalized.toLowerCase() !== "unknown" &&
      !looksLikeCoordinateLabel(normalized)
    ) {
      return normalized;
    }
  }

  return "";
}

function pickCountryFromLocationLabel(...values) {
  const label = pickLocationLabel(...values);
  if (!label) return "";

  const parts = label
    .split(",")
    .map(normalizeTextValue)
    .filter(Boolean);

  if (parts.length < 2) return "";

  return parts[parts.length - 1];
}

function resolveUserAvatarSrc(authorUser) {
  const avatar = normalizeTextValue(authorUser?.avatar);
  if (!avatar) return DEFAULT_AVATAR;
  return /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
}

function normalizeAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveAvatarObjectPosition(authorUser) {
  const positionX = normalizeAvatarPosition(authorUser?.avatarPositionX);
  const positionY = normalizeAvatarPosition(authorUser?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveAvatarScale(authorUser) {
  const parsed = Number(authorUser?.avatarScale);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

function buildBadgeUrl(badgeKey) {
  const normalizedKey = normalizeTextValue(badgeKey).toLowerCase();
  if (!normalizedKey) return "";
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`;
}

function getPostLocationLabel(post) {
  const catchData = post?.catch || {};
  if (catchData.isLocationPublic === false) return "";

  const explicitCountry = pickLocationLabel(
    catchData.country,
    post?.country,
    catchData.historicalContext?.country,
    post?.historicalContext?.country
  );

  if (explicitCountry) return explicitCountry;

  return pickCountryFromLocationLabel(
    catchData.locationLabel,
    post?.locationLabel,
    catchData.historicalContext?.locationLabel,
    post?.historicalContext?.locationLabel,
    catchData.placeName,
    post?.placeName,
    catchData.location,
    post?.location
  );
}

function getPostDateLabel(post, locale) {
  if (!post?.postDate) return "";

  const parsedDate = new Date(post.postDate);
  if (Number.isNaN(parsedDate.getTime())) return normalizeTextValue(post.postDate);

  return parsedDate.toLocaleString(locale || undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildPostChips(post, t) {
  const catchData = post?.catch || {};
  const chipSources = [
    catchData.fish,
    catchData.wayOfFishing,
    catchData.typeOfFishing,
  ];
  const seen = new Set();

  return chipSources
    .map((value) => normalizeTextValue(value))
    .filter(hasMeaningfulValue)
    .filter((value) => value.toLowerCase() !== "unknown")
    .map((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return "";
      seen.add(key);
      return translateFeedValue(t, value);
    })
    .filter(Boolean)
    .slice(0, 4);
}

function isSuggestedFeedPost(post) {
  const feedLabel = String(post?.feedLabel || post?.feedContext?.label || "").trim();
  return feedLabel.toLowerCase() === "suggested";
}

function getPostAnalyticsMetadata(post, source) {
  return {
    source,
    post_type: post?.postType || (post?.catch ? "catch" : "post"),
    is_suggested: isSuggestedFeedPost(post),
    has_media: Boolean(resolvePostMediaSrc(post)),
    is_shared_post: Boolean(post?.sharedPost),
  };
}

function FeedInfoActionIcon() {
  return (
    <svg
      className="rp-post__action-icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20zM12 8h.01M12 12v4" />
    </svg>
  );
}

function isFeedDarkThemeActive() {
  return (
    typeof document !== "undefined" &&
    document.documentElement?.dataset?.theme === "dark"
  );
}

export default function Feed({
  user,
  posts,
  hasMorePosts = false,
  isLoadingMorePosts = false,
  onLoadMorePosts,
  users = [],
  setShowProfile,
  setShowCreatePost,
  setSelectedUser,
  setShowUserProfile,
  setSelectedPost,
  setShowInfo,
  setSharePost,
  onDeletePost,
}) {
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomVideo, setZoomVideo] = useState(null);
  const [zoomVideoPlayFailed, setZoomVideoPlayFailed] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [openPostMenuId, setOpenPostMenuId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [announcement, setAnnouncement] = useState(null);
  const [announcementHidden, setAnnouncementHidden] = useState(false);
  const [announcementDismissPending, setAnnouncementDismissPending] =
    useState(false);
  const [announcementMarkdownBody, setAnnouncementMarkdownBody] = useState("");
  const [mobileFeedViewport, setMobileFeedViewport] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 768px)").matches
  );
  const [mobilePreviewMetadataPostIds, setMobilePreviewMetadataPostIds] =
    useState(() => new Set());
  const [isDarkFeedTheme, setIsDarkFeedTheme] = useState(isFeedDarkThemeActive);
  const zoomVideoRef = useRef(null);
  const shouldAttemptZoomVideoPlayRef = useRef(false);
  const zoomVideoPlayAttemptInFlightRef = useRef(false);
  const zoomVideoOpenIdRef = useRef(0);
  const zoomVideoOpenedAtRef = useRef(0);
  const previousZoomVideoRef = useRef(null);
  const mobileVideoPreviewRefs = useRef({});

  const { t, i18n } = useTranslation();
  const { togglePostLike, isPostLikePending } =
    useOrdinaryPostLikeMutation();
  const { isVisible: showScrollToTop, scrollToTop } = useScrollToTopButton();
  const zoomVideoUrl = zoomVideo?.url || "";
  const zoomVideoInstanceId = zoomVideo?.instanceId || 0;

  const playZoomVideo = useCallback((targetVideo) => {
    const video = targetVideo || zoomVideoRef.current;
    debugFeedVideo("playZoomVideo called", {
      hasTargetVideo: Boolean(targetVideo),
      hasRefVideo: Boolean(zoomVideoRef.current),
      instanceId: zoomVideoInstanceId,
      playFlag: shouldAttemptZoomVideoPlayRef.current,
    });
    if (!video) {
      debugFeedVideo("playZoomVideo missing video ref");
      return;
    }

    try {
      const playInstanceId = zoomVideoInstanceId;
      const isCurrentPlayAttempt = () =>
        zoomVideoOpenIdRef.current === playInstanceId &&
        zoomVideoRef.current === video;
      debugFeedVideo("video.play() about to run", getFeedVideoDebugSnapshot(video));
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise
          .then(() => {
            if (!isCurrentPlayAttempt()) {
              debugFeedVideo("video.play() promise resolved for stale viewer", {
                instanceId: playInstanceId,
                currentInstanceId: zoomVideoOpenIdRef.current,
              });
              return;
            }
            shouldAttemptZoomVideoPlayRef.current = false;
            zoomVideoPlayAttemptInFlightRef.current = false;
            setZoomVideoPlayFailed(false);
            debugFeedVideo(
              "video.play() promise resolved",
              {
                instanceId: zoomVideoInstanceId,
                video: getFeedVideoDebugSnapshot(video),
              }
            );
          })
          .catch((error) => {
            if (!isCurrentPlayAttempt()) {
              debugFeedVideo("video.play() promise rejected for stale viewer", {
                instanceId: playInstanceId,
                currentInstanceId: zoomVideoOpenIdRef.current,
                name: error?.name || "",
                message: error?.message || "",
              });
              return;
            }
            shouldAttemptZoomVideoPlayRef.current = false;
            zoomVideoPlayAttemptInFlightRef.current = false;
            setZoomVideoPlayFailed(true);
            debugFeedVideo("video.play() promise rejected", {
              instanceId: zoomVideoInstanceId,
              name: error?.name || "",
              message: error?.message || "",
              video: getFeedVideoDebugSnapshot(video),
              viewerRemainsOpen: true,
            });
            // Mobile browsers may block playback with sound; controls remain visible.
          });
      } else {
        shouldAttemptZoomVideoPlayRef.current = false;
        zoomVideoPlayAttemptInFlightRef.current = false;
        setZoomVideoPlayFailed(false);
        debugFeedVideo(
          "video.play() returned without promise",
          {
            instanceId: zoomVideoInstanceId,
            video: getFeedVideoDebugSnapshot(video),
          }
        );
      }
    } catch (error) {
      shouldAttemptZoomVideoPlayRef.current = false;
      zoomVideoPlayAttemptInFlightRef.current = false;
      setZoomVideoPlayFailed(true);
      debugFeedVideo("video.play() threw", {
        instanceId: zoomVideoInstanceId,
        name: error?.name || "",
        message: error?.message || "",
        video: getFeedVideoDebugSnapshot(video),
        viewerRemainsOpen: true,
      });
      // Controls remain visible if playback cannot start automatically.
    }
  }, [zoomVideoInstanceId]);

  const pauseVideoElement = useCallback((video, options = {}) => {
    if (!video) {
      return {
        before: getFeedVideoDebugSnapshot(video),
        after: getFeedVideoDebugSnapshot(video),
        error: null,
      };
    }

    const before = getFeedVideoDebugSnapshot(video);
    let errorDetails = null;
    try {
      video.pause();
      if (options.reset) {
        video.currentTime = 0;
      }
    } catch (error) {
      errorDetails = {
        name: error?.name || "",
        message: error?.message || "",
      };
      // Closing/opening continues even if the browser refuses media control.
    }

    return {
      before,
      after: getFeedVideoDebugSnapshot(video),
      error: errorDetails,
    };
  }, []);

  const hardStopVideoElement = useCallback((video, reason = "unspecified") => {
    const before = getFeedVideoDebugSnapshot(video);
    const beforeAttributeSrc = video?.getAttribute?.("src") || "";
    const beforeSources =
      video && typeof video.querySelectorAll === "function"
        ? Array.from(video.querySelectorAll("source"))
        : [];
    const result = {
      reason,
      before,
      beforeAttributeSrc: shortenFeedVideoUrl(beforeAttributeSrc),
      beforeSourceCount: beforeSources.length,
      pauseError: null,
      muteError: null,
      resetError: null,
      clearSourceError: null,
      loadCalled: false,
      loadError: null,
      afterAttributeSrc: "",
      after: null,
    };

    debugFeedVideo("hardStopVideoElement started", result);

    if (!video) {
      debugFeedVideo("hardStopVideoElement skipped: missing video", result);
      return result;
    }

    try {
      video.pause();
    } catch (error) {
      result.pauseError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.muted = true;
    } catch (error) {
      result.muteError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.currentTime = 0;
    } catch (error) {
      result.resetError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    try {
      video.removeAttribute("src");
      beforeSources.forEach((source) => {
        source.removeAttribute("src");
      });
    } catch (error) {
      result.clearSourceError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    result.afterAttributeSrc = video.getAttribute("src") || "";
    debugFeedVideo("hardStopVideoElement source cleared", {
      reason,
      srcBefore: result.beforeAttributeSrc,
      srcAfter: result.afterAttributeSrc,
      currentSrcAfterClear: shortenFeedVideoUrl(video.currentSrc || ""),
      sourceCount: beforeSources.length,
    });

    try {
      video.load();
      result.loadCalled = true;
    } catch (error) {
      result.loadError = {
        name: error?.name || "",
        message: error?.message || "",
      };
    }

    result.after = getFeedVideoDebugSnapshot(video);
    debugFeedVideo("hardStopVideoElement finished", result);
    return result;
  }, []);

  const cleanupFeedVideoMediaSession = useCallback((reason = "unspecified") => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) {
      debugFeedVideo("mediaSession cleanup skipped", {
        reason,
        hasNavigator: typeof navigator !== "undefined",
      });
      return;
    }

    try {
      navigator.mediaSession.playbackState = "none";
      debugFeedVideo("mediaSession playbackState set to none", { reason });
    } catch (error) {
      debugFeedVideo("mediaSession playbackState cleanup failed", {
        reason,
        name: error?.name || "",
        message: error?.message || "",
      });
    }

    try {
      navigator.mediaSession.metadata = null;
      debugFeedVideo("mediaSession metadata cleared", { reason });
    } catch (error) {
      debugFeedVideo("mediaSession metadata cleanup failed", {
        reason,
        name: error?.name || "",
        message: error?.message || "",
      });
    }
  }, []);

  const pauseFeedVideos = useCallback(
    (options = {}) => {
      debugFeedVideo("pauseFeedVideos start", options);
      if (typeof document === "undefined") {
        debugFeedVideo("pauseFeedVideos skipped: no document");
        return;
      }

      const allVideos = Array.from(
        document.querySelectorAll(".rp-post__media--video")
      );
      const viewerVideo = zoomVideoRef.current;
      const videos = allVideos.filter((video) => video !== viewerVideo);
      debugFeedVideo("pauseFeedVideos found embedded videos", {
        totalCount: allVideos.length,
        count: videos.length,
        skippedViewerVideo: allVideos.includes(viewerVideo),
        massCurrentTimeResetSkipped: !options.reset,
      });

      videos.forEach((video, index) => {
        const result = pauseVideoElement(video, options);
        debugFeedVideo("pauseFeedVideos item", {
          index,
          className: video.className,
          result,
        });
      });

      debugFeedVideo("pauseFeedVideos end", { count: videos.length });
    },
    [pauseVideoElement]
  );

  const attemptZoomVideoPlay = useCallback((reason = "unspecified") => {
    debugFeedVideo("attemptZoomVideoPlay called", {
      reason,
      instanceId: zoomVideoInstanceId,
      shouldAttemptPlay: shouldAttemptZoomVideoPlayRef.current,
      playAttemptInFlight: zoomVideoPlayAttemptInFlightRef.current,
    });
    if (!shouldAttemptZoomVideoPlayRef.current) return;
    if (zoomVideoPlayAttemptInFlightRef.current) {
      debugFeedVideo("attemptZoomVideoPlay skipped: play attempt in flight", {
        reason,
      });
      return;
    }

    const video = zoomVideoRef.current;
    if (!video) {
      debugFeedVideo("attemptZoomVideoPlay missing video ref");
      return;
    }

    zoomVideoPlayAttemptInFlightRef.current = true;
    playZoomVideo(video);
  }, [playZoomVideo, zoomVideoInstanceId]);

  const openZoomVideo = (videoSrc, options = {}) => {
    const nextInstanceId = zoomVideoOpenIdRef.current + 1;
    zoomVideoOpenIdRef.current = nextInstanceId;
    zoomVideoOpenedAtRef.current =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    debugFeedVideo("openZoomVideo called", {
      videoSrc: shortenFeedVideoUrl(videoSrc),
      instanceId: nextInstanceId,
      eventType: options.eventType || "",
      postId: options.postId || "",
    });
    debugFeedVideo("openZoomVideo calling pauseFeedVideos");
    pauseFeedVideos({ reset: false, reason: "open_viewer" });
    shouldAttemptZoomVideoPlayRef.current = true;
    zoomVideoPlayAttemptInFlightRef.current = false;
    setZoomVideoPlayFailed(false);

    debugFeedVideo("openZoomVideo calling setZoomVideo", {
      videoSrc: shortenFeedVideoUrl(videoSrc),
      instanceId: nextInstanceId,
    });
    flushSync(() => {
      setZoomVideo({
        url: videoSrc,
        instanceId: nextInstanceId,
      });
    });
    debugFeedVideo("openZoomVideo setZoomVideo returned", {
      videoSrc: shortenFeedVideoUrl(videoSrc),
      instanceId: nextInstanceId,
      hasViewerRef: Boolean(zoomVideoRef.current),
      shouldAttemptPlay: shouldAttemptZoomVideoPlayRef.current,
    });
    debugFeedVideo("openZoomVideo waiting for viewer media readiness");
  };

  const closeZoomVideo = (options = {}) => {
    const reason = options.reason || "unspecified";
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    const elapsedSinceOpen = now - zoomVideoOpenedAtRef.current;
    debugFeedVideo("closeZoomVideo called", {
      hasViewerRef: Boolean(zoomVideoRef.current),
      zoomVideo: shortenFeedVideoUrl(zoomVideoUrl),
      instanceId: zoomVideoInstanceId,
      reason,
      elapsedSinceOpen: Number.isFinite(elapsedSinceOpen)
        ? Number(elapsedSinceOpen.toFixed(1))
        : elapsedSinceOpen,
    });

    if (
      reason === "backdrop" &&
      elapsedSinceOpen >= 0 &&
      elapsedSinceOpen < ZOOM_VIDEO_BACKDROP_CLOSE_GUARD_MS
    ) {
      debugFeedVideo("closeZoomVideo ignored: backdrop close guard", {
        instanceId: zoomVideoInstanceId,
        elapsedSinceOpen,
      });
      return;
    }

    shouldAttemptZoomVideoPlayRef.current = false;
    zoomVideoPlayAttemptInFlightRef.current = false;
    setZoomVideoPlayFailed(false);

    if (
      typeof document !== "undefined" &&
      document.fullscreenElement &&
      typeof document.exitFullscreen === "function"
    ) {
      try {
        const exitPromise = document.exitFullscreen();
        if (exitPromise && typeof exitPromise.catch === "function") {
          exitPromise.catch(() => {});
        }
      } catch {
        // Native fullscreen exit is defensive only.
      }
    }

    const video = zoomVideoRef.current;
    debugFeedVideo("closeZoomVideo hard stopping viewer video");
    debugFeedVideo("closeZoomVideo hard stop viewer result", {
      result: hardStopVideoElement(video, "close_zoom_video"),
      instanceId: zoomVideoInstanceId,
    });
    zoomVideoRef.current = null;
    debugFeedVideo("closeZoomVideo cleared viewer ref after hard stop", {
      instanceId: zoomVideoInstanceId,
    });

    cleanupFeedVideoMediaSession("close_zoom_video");

    debugFeedVideo("closeZoomVideo calling setZoomVideo(null)");
    setZoomVideo(null);
    zoomVideoOpenedAtRef.current = 0;
  };

  const handleFeedVideoTap = (event, videoSrc, options = {}) => {
    const previewVideo = getFeedVideoEventPreviewVideo(event);
    debugFeedVideo("handleFeedVideoTap fired", {
      videoSrc: shortenFeedVideoUrl(videoSrc),
      postId: options.postId || "",
      hasPoster: Boolean(options.posterSrc),
      posterField: options.posterField || "",
      posterSrc: shortenFeedVideoUrl(options.posterSrc),
      configuredPreviewPreload: options.previewPreload || "",
      metadataPreviewEnabled: Boolean(options.metadataPreviewEnabled),
      mobileFeedViewport,
      windowInnerWidth:
        typeof window !== "undefined" ? window.innerWidth : undefined,
      targetTagName: event?.target?.tagName || "",
      targetClassName: String(event?.target?.className || ""),
      previewPreload: previewVideo?.preload || "",
      previewControls: Boolean(previewVideo?.controls),
      preview: getFeedVideoDebugSnapshot(previewVideo),
    });
    if (!mobileFeedViewport) {
      debugFeedVideo("handleFeedVideoTap ignored: desktop viewport");
      return;
    }

    debugFeedVideo("handleFeedVideoTap preventing default and propagation");
    event.preventDefault();
    event.stopPropagation();
    debugFeedVideo("handleFeedVideoTap opening viewer; openZoomVideo will pause previews");
    debugFeedVideo("handleFeedVideoTap calling openZoomVideo");
    openZoomVideo(videoSrc, {
      eventType: event?.type || "",
      postId: options.postId || "",
    });
  };

  const setZoomVideoNode = useCallback(
    (node) => {
      zoomVideoRef.current = node;
      debugFeedVideo(node ? "viewer video mounted" : "viewer video unmounted", {
        zoomVideo: shortenFeedVideoUrl(zoomVideoUrl),
        instanceId: zoomVideoInstanceId,
        video: getFeedVideoDebugSnapshot(node),
      });
    },
    [zoomVideoInstanceId, zoomVideoUrl]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const updateMobileFeedViewport = () => {
      debugFeedVideo("mobile viewport media query changed", {
        matches: mediaQuery.matches,
        windowInnerWidth: window.innerWidth,
      });
      setMobileFeedViewport(mediaQuery.matches);
    };

    updateMobileFeedViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMobileFeedViewport);
      return () => {
        mediaQuery.removeEventListener("change", updateMobileFeedViewport);
      };
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(updateMobileFeedViewport);
      return () => {
        mediaQuery.removeListener(updateMobileFeedViewport);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    debugFeedVideo("mobileFeedViewport state changed", {
      mobileFeedViewport,
      windowInnerWidth:
        typeof window !== "undefined" ? window.innerWidth : undefined,
    });
  }, [mobileFeedViewport]);

  useEffect(() => {
    if (!mobileFeedViewport) return;

    const videoPosterRows = posts
      .filter((post) => isVideoPostMedia(post))
      .map((post) => {
        const poster = resolveFeedVideoPoster(post);
        return {
          postId: String(post?.id || ""),
          hasPoster: Boolean(poster.src),
          posterField: poster.field,
          posterSrc: shortenFeedVideoUrl(poster.src),
          mediaSrc: shortenFeedVideoUrl(resolvePostMediaSrc(post)),
        };
      });

    debugFeedVideo("mobile preview poster scan", {
      videoCount: videoPosterRows.length,
      withPosterCount: videoPosterRows.filter((row) => row.hasPoster).length,
      withoutPosterCount: videoPosterRows.filter((row) => !row.hasPoster).length,
      examples: videoPosterRows.slice(0, 8),
    });
  }, [mobileFeedViewport, posts]);

  useEffect(() => {
    if (!mobileFeedViewport) {
      setMobilePreviewMetadataPostIds((current) => {
        if (current.size === 0) return current;
        debugFeedVideo("mobile preview metadata reset for desktop viewport", {
          previousCount: current.size,
        });
        return new Set();
      });
      return undefined;
    }

    const activePostIds = new Set(
      posts.map((post) => String(post?.id || "")).filter(Boolean)
    );

    setMobilePreviewMetadataPostIds((current) => {
      const next = new Set(
        Array.from(current).filter((postId) => activePostIds.has(postId))
      );
      if (next.size === current.size) return current;
      debugFeedVideo("mobile preview metadata pruned for current posts", {
        previousCount: current.size,
        nextCount: next.size,
      });
      return next;
    });

    const previewEntries = Object.entries(mobileVideoPreviewRefs.current).filter(
      ([postId, element]) => activePostIds.has(postId) && element
    );

    debugFeedVideo("mobile preview observer setup", {
      previewCount: previewEntries.length,
      hasIntersectionObserver:
        typeof window !== "undefined" && "IntersectionObserver" in window,
    });

    if (previewEntries.length === 0) return undefined;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setMobilePreviewMetadataPostIds((current) => {
        const next = new Set(current);
        previewEntries.forEach(([postId]) => next.add(postId));
        debugFeedVideo("mobile preview metadata fallback enabled", {
          count: next.size,
        });
        return next;
      });
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const visiblePostIds = entries
          .filter((entry) => entry.isIntersecting || entry.intersectionRatio > 0)
          .map((entry) =>
            String(entry.target.getAttribute("data-feed-video-post-id") || "")
          )
          .filter(Boolean);

        if (visiblePostIds.length === 0) return;

        setMobilePreviewMetadataPostIds((current) => {
          let changed = false;
          const next = new Set(current);
          visiblePostIds.forEach((postId) => {
            if (!next.has(postId)) {
              next.add(postId);
              changed = true;
            }
          });

          if (!changed) return current;

          debugFeedVideo("mobile preview metadata enabled near viewport", {
            postIds: visiblePostIds,
            nextCount: next.size,
            preloadMode: "metadata",
          });
          return next;
        });
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0.01,
      }
    );

    previewEntries.forEach(([, element]) => observer.observe(element));

    return () => observer.disconnect();
  }, [mobileFeedViewport, posts]);

  useEffect(() => {
    debugFeedVideo("mobile preview metadata set changed", {
      count: mobilePreviewMetadataPostIds.size,
      postIds: Array.from(mobilePreviewMetadataPostIds).slice(0, 12),
    });
  }, [mobilePreviewMetadataPostIds]);

  useEffect(() => {
    const previousZoomVideo = previousZoomVideoRef.current;
    const previousZoomVideoUrl = previousZoomVideo?.url || "";
    debugFeedVideo("zoomVideo state changed", {
      previous: shortenFeedVideoUrl(previousZoomVideoUrl),
      next: shortenFeedVideoUrl(zoomVideoUrl),
      previousInstanceId: previousZoomVideo?.instanceId || 0,
      nextInstanceId: zoomVideoInstanceId,
      opened: Boolean(zoomVideo && !previousZoomVideo),
      closed: Boolean(previousZoomVideo && !zoomVideo),
    });
    previousZoomVideoRef.current = zoomVideo;
  }, [zoomVideo, zoomVideoInstanceId, zoomVideoUrl]);

  useEffect(() => {
    if (!zoomVideo) return;

    debugFeedVideo("zoomVideo active; waiting for viewer media events", {
      zoomVideo: shortenFeedVideoUrl(zoomVideoUrl),
      instanceId: zoomVideoInstanceId,
      shouldAttemptPlay: shouldAttemptZoomVideoPlayRef.current,
    });
  }, [zoomVideo, zoomVideoInstanceId, zoomVideoUrl]);

  useEffect(() => {
    if (!zoomVideo) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    if (!body || !documentElement) return undefined;

    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      documentElement.scrollTop ||
      body.scrollTop ||
      0;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    const previousDocumentStyles = {
      overflow: documentElement.style.overflow,
      overscrollBehavior: documentElement.style.overscrollBehavior,
    };

    debugFeedVideo("zoomVideo scroll lock applied", {
      instanceId: zoomVideoInstanceId,
      scrollY,
      zoomVideo: shortenFeedVideoUrl(zoomVideoUrl),
    });

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior;
      documentElement.style.overflow = previousDocumentStyles.overflow;
      documentElement.style.overscrollBehavior =
        previousDocumentStyles.overscrollBehavior;

      try {
        window.scrollTo(0, scrollY);
      } catch {
        // Restoring styles is still useful if scroll restoration is unavailable.
      }

      debugFeedVideo("zoomVideo scroll lock restored", {
        instanceId: zoomVideoInstanceId,
        scrollY,
      });
    };
  }, [zoomVideo, zoomVideoInstanceId, zoomVideoUrl]);

  const logZoomVideoEvent = useCallback((eventName, event) => {
    const video = event?.currentTarget || zoomVideoRef.current;
    debugFeedVideo(`viewer video ${eventName}`, {
      instanceId: zoomVideoInstanceId,
      video: getFeedVideoDebugSnapshot(video),
      playFailed: zoomVideoPlayFailed,
    });
  }, [zoomVideoInstanceId, zoomVideoPlayFailed]);

  const handleZoomVideoReadyEvent = useCallback(
    (event) => {
      logZoomVideoEvent(event.type, event);
      debugFeedVideo("viewer readiness event may trigger play attempt", {
        eventType: event.type,
        instanceId: zoomVideoInstanceId,
        shouldAttemptPlay: shouldAttemptZoomVideoPlayRef.current,
      });
      attemptZoomVideoPlay(event.type);
    },
    [attemptZoomVideoPlay, logZoomVideoEvent, zoomVideoInstanceId]
  );

  const handleZoomVideoPlayingEvent = useCallback(
    (event) => {
      logZoomVideoEvent("playing", event);
      shouldAttemptZoomVideoPlayRef.current = false;
      zoomVideoPlayAttemptInFlightRef.current = false;
      setZoomVideoPlayFailed(false);
      debugFeedVideo("viewer playing; cleared play failure state", {
        instanceId: zoomVideoInstanceId,
      });
    },
    [logZoomVideoEvent, zoomVideoInstanceId]
  );

  const handleZoomVideoFallbackPlay = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      debugFeedVideo("viewer fallback play tapped", {
        instanceId: zoomVideoInstanceId,
        zoomVideo: shortenFeedVideoUrl(zoomVideoUrl),
        hasViewerRef: Boolean(zoomVideoRef.current),
      });
      setZoomVideoPlayFailed(false);
      shouldAttemptZoomVideoPlayRef.current = true;
      zoomVideoPlayAttemptInFlightRef.current = false;
      attemptZoomVideoPlay("fallback_tap");
    },
    [attemptZoomVideoPlay, zoomVideoInstanceId, zoomVideoUrl]
  );

  useEffect(() => {
    setAnnouncementHidden(false);
  }, [announcement?.id]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    setAnnouncementMarkdownBody("");

    if (!announcement?.contentKey) {
      return () => controller.abort();
    }

    async function loadAnnouncementMarkdown() {
      try {
        const markdown = await fetchAnnouncementMarkdownWithFallback(
          announcement.contentKey,
          i18n.language,
          { signal: controller.signal }
        );
        if (!cancelled) {
          setAnnouncementMarkdownBody(markdown);
        }
      } catch (err) {
        if (err?.name !== "AbortError" && !cancelled) {
          setAnnouncementMarkdownBody("");
        }
      }
    }

    loadAnnouncementMarkdown();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [announcement?.contentKey, announcement?.contentVersion, i18n.language]);

  useEffect(() => {
    document.body.classList.add("feed-page");
    return () => {
      document.body.classList.remove("feed-page");
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    const root = document.documentElement;
    const updateThemeState = () => {
      setIsDarkFeedTheme(isFeedDarkThemeActive());
    };

    updateThemeState();

    if (typeof window.MutationObserver !== "function") {
      return undefined;
    }

    const observer = new window.MutationObserver(updateThemeState);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const map = {};
    (users || []).forEach((loadedUser) => {
      if (loadedUser?.id) {
        map[loadedUser.id] = loadedUser;
      }
    });
    setUsersMap(map);
  }, [users]);

  const usersByUsername = useMemo(() => {
    const map = new Map();

    if (user?.username) {
      map.set(String(user.username).toLowerCase(), user);
    }

    (users || []).forEach((loadedUser) => {
      const username = normalizeTextValue(loadedUser?.username);
      if (username) {
        map.set(username.toLowerCase(), loadedUser);
      }
    });

    return map;
  }, [user, users]);

  useEffect(() => {
    let cancelled = false;
    const authToken = getActiveAwareAuthToken(window);

    if (!user?.id) {
      setAnnouncement(null);
      setAnnouncementHidden(false);
      setAnnouncementDismissPending(false);
      return undefined;
    }

    async function loadAnnouncement() {
      try {
        const payload = await getCurrentAnnouncement(authToken || "");
        if (cancelled) return;
        setAnnouncement(payload?.announcement || null);
      } catch (err) {
        if (!cancelled) {
          setAnnouncement(null);
        }
      }
    }

    loadAnnouncement();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const visibleAnnouncement =
    announcement && !announcementHidden ? announcement : null;

  const announcementBodyHtml = useMemo(() => {
    if (!visibleAnnouncement?.contentKey || !announcementMarkdownBody.trim()) {
      return "";
    }

    try {
      return marked.parse(announcementMarkdownBody);
    } catch (err) {
      return "";
    }
  }, [announcementMarkdownBody, visibleAnnouncement?.contentKey]);

  const handleDismissAnnouncement = async () => {
    const authToken = getActiveAwareAuthToken(window);
    if (!user?.id || !announcement?.id || announcementDismissPending) {
      return;
    }

    const currentAnnouncement = announcement;
    setAnnouncementDismissPending(true);
    setAnnouncementHidden(true);

    try {
      await dismissCurrentAnnouncement(authToken || "");
      setAnnouncement(null);
    } catch (err) {
      setAnnouncement(currentAnnouncement);
      setAnnouncementHidden(false);
    } finally {
      setAnnouncementDismissPending(false);
    }
  };

  const handleOpenInfo = (post) => {
    trackProductEvent("post_info_opened", {
      featureArea: "feed",
      metadata: getPostAnalyticsMetadata(post, "info_button"),
    });
    if (isSuggestedFeedPost(post)) {
      trackProductEvent("suggested_post_clicked", {
        featureArea: "feed",
        metadata: getPostAnalyticsMetadata(post, "info_button"),
      });
    }
    setSelectedPost(post);
    setShowInfo(true);
  };

  const handleOpenAuthorProfile = (post, source = "author_name") => {
    if (isSuggestedFeedPost(post)) {
      trackProductEvent("suggested_post_clicked", {
        featureArea: "feed",
        metadata: getPostAnalyticsMetadata(post, source),
      });
    }

    if (post.user === user?.username) {
      setShowProfile(true);
    } else if (post.user === "Trip Catch") {
      return;
    } else {
      setSelectedUser(post.user);
      setShowUserProfile(true);
    }
  };

  const handleDeletePostFromMenu = async (post) => {
    if (!post?.id || typeof onDeletePost !== "function" || deletingPostId) return;

    const confirmed = window.confirm(
      t("profile_delete_post_confirm", {
        defaultValue: "Delete this post?",
      })
    );
    if (!confirmed) return;

    setDeletingPostId(post.id);
    try {
      const deleteResult = await onDeletePost(post.id);
      const didDelete = deleteResult === true || deleteResult?.ok === true;
      if (!didDelete) {
        window.alert(
          deleteResult?.message ||
            t("profile_delete_post_failed", {
              defaultValue: "Failed to delete post.",
            })
        );
      }
    } finally {
      setDeletingPostId(null);
      setOpenPostMenuId(null);
    }
  };

  const shareCountLookup = buildShareCountLookup(posts);

  return (
    <div className="feed-container rp-feed-shell">
      <ScrollToTopButton
        isVisible={showScrollToTop}
        onClick={scrollToTop}
        label={t("scroll_to_top", { defaultValue: "Scroll to top" })}
      />

      <div className="feed rp-feed">
        <h2 className="rp-feed__title">
          {t("feed_title")}
        </h2>

        <section
          className="feed-create-post-row rp-composer"
          aria-label={t("feed_composer_aria_label", {
            defaultValue:
              "Share your adventure. Share your catch, opinion or fishing story with the community.",
          })}
          style={{
            "--rp-create-post-banner-image": isDarkFeedTheme
              ? CREATE_POST_HERO_IMAGE_DARK
              : CREATE_POST_HERO_IMAGE,
          }}
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/847/847969.png"
            alt={t("feed_profile_avatar_alt")}
            className="feed-create-post-avatar rp-composer__avatar"
            onClick={() => setShowProfile(true)}
          />

          <button
            type="button"
            className="feed-create-post-button rp-composer__btn"
            onClick={() => setShowCreatePost(true)}
            aria-label={t("feed_composer_button_label", {
              defaultValue: "Post a catch or fishing story",
            })}
            title={t("feed_composer_button_label", {
              defaultValue: "Post a catch or fishing story",
            })}
          >
            <span className="rp-composer__icon" aria-hidden="true">
              +
            </span>
            <span className="rp-composer__label rp-composer__label--desktop">
              {t("groups_create_post", { defaultValue: "Create Post" })}
            </span>
            <span className="rp-composer__label rp-composer__label--mobile">
              {t("feed_create_post_mobile_label", {
                defaultValue: "Create Post",
              })}
            </span>
          </button>
        </section>

        {visibleAnnouncement ? (
          <section
            className="post feed-announcement-card rp-announcement"
            aria-label={t("feed_announcement", { defaultValue: "Announcement" })}
          >
            <div className="feed-announcement-card__header">
              <div className="feed-announcement-card__eyebrow">
                {t("feed_announcement", { defaultValue: "Announcement" })}
              </div>
              <button
                type="button"
                className="feed-announcement-card__close rp-announcement__dismiss"
                onClick={handleDismissAnnouncement}
                disabled={announcementDismissPending}
                aria-label={t("common_close")}
              >
                {"\u00D7"}
              </button>
            </div>

            <div className="rp-announcement__body">
              <div>
                <h3 className="feed-announcement-card__title rp-announcement__title">
                  {visibleAnnouncement.title}
                </h3>
                {announcementBodyHtml ? (
                  <div
                    className="feed-announcement-card__body feed-announcement-card__body--markdown rp-announcement__desc"
                    dangerouslySetInnerHTML={{ __html: announcementBodyHtml }}
                  />
                ) : (
                  <p className="feed-announcement-card__body rp-announcement__desc">
                    {visibleAnnouncement.body}
                  </p>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {posts.length === 0 ? (
          <p className="rp-feed__empty">
            {t("no_results", "No results found")}
          </p>
        ) : null}

        {posts.map((post) => {
          const isSuggestedPost = isSuggestedFeedPost(post);
          const sharedPostMediaSrc = resolvePostMediaSrc(post.sharedPost);
          const postMediaSrc = resolvePostMediaSrc(post);
          const postHasVideoMedia = isVideoPostMedia(post);
          const postVideoPoster = postHasVideoMedia
            ? resolveFeedVideoPoster(post)
            : { src: "", field: "" };
          const postId = String(post.id || "");
          const mobilePreviewMetadataEnabled =
            mobileFeedViewport &&
            !postVideoPoster.src &&
            mobilePreviewMetadataPostIds.has(postId);
          const postVideoPreviewPreload = mobileFeedViewport
            ? postVideoPoster.src
              ? "none"
              : mobilePreviewMetadataEnabled
              ? "metadata"
              : "none"
            : "metadata";
          const authorUser =
            usersByUsername.get(normalizeTextValue(post.user).toLowerCase()) || null;
          const authorProgression = authorUser?.progression || post?.progression || {};
          const authorRankName = normalizeTextValue(
            authorProgression.currentRankName || authorProgression.rankName
          );
          const authorBadgeUrl = buildBadgeUrl(
            authorProgression.currentBadgeKey || authorProgression.badgeKey
          );
          const authorAvatarStyle = {
            objectPosition: resolveAvatarObjectPosition(authorUser),
            transform: `scale(${resolveAvatarScale(authorUser)})`,
          };
          const locationLabel = getPostLocationLabel(post);
          const dateLabel = getPostDateLabel(post, i18n.language);
          const postChips = buildPostChips(post, t);
          const likeCount = post.likes?.length || 0;
          const commentCount = post.comments?.length || 0;
          const shareCount = getShareCountForPost(post, shareCountLookup);
          const currentUserHasLikedPost = Boolean(
            user?.id &&
              Array.isArray(post.likes) &&
              post.likes.some((likeUserId) => String(likeUserId) === String(user.id))
          );
          const postLikePending = isPostLikePending(post.id);
          const canShowPostMenu = Boolean(
            typeof onDeletePost === "function" &&
              user?.id &&
              (String(post.userId || "") === String(user.id) ||
                String(post.user || "") === String(user.username || ""))
          );
          const hasPostText = Boolean(post.sharedPost || hasMeaningfulValue(post.caption));
          const postContentClassName = [
            "rp-post__content",
            postMediaSrc ? "rp-post__content--with-media" : "",
            postMediaSrc && !hasPostText ? "rp-post__content--media-only" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const postActionsClassName = [
            "post-actions",
            "rp-post__actions",
            post.postType !== "opinion"
              ? "rp-post__actions--has-info"
              : "rp-post__actions--no-info",
          ].join(" ");

          return (
            <div key={post.id} className="post rp-post">
              <div className="rp-post__header">
                <button
                  type="button"
                  className="rp-post__avatar-button"
                  onClick={() => handleOpenAuthorProfile(post, "author_avatar")}
                  disabled={post.user === "Trip Catch"}
                  aria-label={t("open_profile", {
                    defaultValue: "Open profile",
                  })}
                >
                  <img
                    src={resolveUserAvatarSrc(authorUser)}
                    alt={t("profile_picture")}
                    className="rp-post__avatar"
                    style={authorAvatarStyle}
                  />
                </button>

                <div className="rp-post__meta">
                  <div className="rp-post__name-row">
                    <h3
                      className="rp-post__name rp-post__profile-link"
                      onClick={() => handleOpenAuthorProfile(post)}
                    >
                      {post.user}
                    </h3>
                    {authorBadgeUrl ? (
                      <img
                        src={authorBadgeUrl}
                        alt={
                          authorRankName
                            ? t("profile_rank_badge_alt", {
                                defaultValue: "{{rankName}} badge",
                                rankName: authorRankName,
                              })
                            : ""
                        }
                        className="rp-post__rank-badge"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    {isSuggestedPost ? (
                      <span className="rp-post__source-chip">
                        {t("feed_label_suggested", {
                          defaultValue: "Suggested",
                        })}
                      </span>
                    ) : null}
                  </div>

                  {authorRankName ? (
                    <span className="rp-post__rank-text">{authorRankName}</span>
                  ) : null}
                  {post.is_pinned === true ? (
                    <span
                      className="rp-post__pinned-chip"
                      aria-label={t("feed_pinned_post_accessible")}
                    >
                      <Pin size={12} strokeWidth={2.2} aria-hidden="true" />
                      <span>{t("feed_pinned_announcement")}</span>
                    </span>
                  ) : null}
                </div>

                <div className="rp-post__header-trail">
                  {dateLabel ? (
                    <span className="rp-post__time-row">
                      <span className="rp-post__time">{dateLabel}</span>
                    </span>
                  ) : null}
                  {locationLabel ? (
                    <span className="rp-post__location">
                      <MapPin size={11} strokeWidth={2.1} aria-hidden="true" />
                      <span>{locationLabel}</span>
                    </span>
                  ) : null}
                </div>

                {canShowPostMenu ? (
                  <div className="rp-post__owner-actions">
                    <button
                      type="button"
                      className="rp-post__menu-btn"
                      aria-label={t("profile_post_actions", {
                        defaultValue: "Post actions",
                      })}
                      aria-haspopup="menu"
                      aria-expanded={openPostMenuId === post.id}
                      onClick={() =>
                        setOpenPostMenuId((current) =>
                          current === post.id ? null : post.id
                        )
                      }
                    >
                      <MoreVertical size={22} strokeWidth={2.4} aria-hidden="true" />
                    </button>

                    {openPostMenuId === post.id ? (
                      <div className="rp-post__menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="rp-post__menu-item rp-post__menu-item--danger"
                          disabled={deletingPostId === post.id}
                          onClick={() => handleDeletePostFromMenu(post)}
                        >
                          {deletingPostId === post.id
                            ? `${t("delete")}...`
                            : t("delete")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className={postContentClassName}>
                {postMediaSrc ? (
                  <div className="rp-post__image">
                    {postHasVideoMedia ? (
                      <div
                        className="rp-post__video-shell"
                        ref={(element) => {
                          if (!postId) return;
                          if (element) {
                            mobileVideoPreviewRefs.current[postId] = element;
                          } else {
                            delete mobileVideoPreviewRefs.current[postId];
                          }
                        }}
                        role={mobileFeedViewport ? "button" : undefined}
                        tabIndex={mobileFeedViewport ? 0 : undefined}
                        aria-label={
                          mobileFeedViewport
                            ? t("open_video", { defaultValue: "Open video" })
                            : undefined
                        }
                        data-feed-video-post-id={postId}
                        data-feed-video-preload={postVideoPreviewPreload}
                        data-feed-video-poster-field={postVideoPoster.field}
                        onPointerUpCapture={(event) =>
                          handleFeedVideoTap(event, postMediaSrc, {
                            postId,
                            posterField: postVideoPoster.field,
                            posterSrc: postVideoPoster.src,
                            previewPreload: postVideoPreviewPreload,
                            metadataPreviewEnabled: mobilePreviewMetadataEnabled,
                          })
                        }
                        onKeyDown={(event) => {
                          if (!mobileFeedViewport) return;
                          if (event.key !== "Enter" && event.key !== " ") return;
                          handleFeedVideoTap(event, postMediaSrc, {
                            postId,
                            posterField: postVideoPoster.field,
                            posterSrc: postVideoPoster.src,
                            previewPreload: postVideoPreviewPreload,
                            metadataPreviewEnabled: mobilePreviewMetadataEnabled,
                          });
                        }}
                      >
                        <video
                          src={postMediaSrc}
                          poster={postVideoPoster.src || undefined}
                          controls={!mobileFeedViewport}
                          controlsList="nodownload noplaybackrate"
                          disablePictureInPicture
                          playsInline
                          preload={postVideoPreviewPreload}
                          className="rp-post__media rp-post__media--video"
                          onPlay={(event) => {
                            if (!mobileFeedViewport) return;
                            debugFeedVideo("mobile preview attempted inline play; pausing", {
                              videoSrc: shortenFeedVideoUrl(postMediaSrc),
                              postId,
                              previewPreload: postVideoPreviewPreload,
                              preview: getFeedVideoDebugSnapshot(event.currentTarget),
                            });
                            pauseVideoElement(event.currentTarget, { reset: false });
                          }}
                        />
                        {mobileFeedViewport ? (
                          <span
                            className="rp-post__video-play"
                            aria-hidden="true"
                          ></span>
                        ) : null}
                      </div>
                    ) : (
                      <img
                        src={postMediaSrc}
                        alt={t("post_media")}
                        className="post-image rp-post__media rp-post__media--image"
                        onClick={() => setZoomImage(postMediaSrc)}
                      />
                    )}
                  </div>
                ) : null}

                {hasPostText ? (
                  <div className="rp-post__text-panel">
                    {post.sharedPost ? (
                      <div className="shared-post-wrapper rp-post__shared">
                        <p className="shared-by rp-post__shared-by">
                          {"\uD83D\uDD01"} <strong>{post.user}</strong> {t("shared")}
                        </p>

                        {post.caption ? (
                          <p className="shared-text rp-post__shared-text">{post.caption}</p>
                        ) : null}

                        <div className="shared-original rp-post__shared-original">
                          <strong>{post.sharedPost.user}</strong>
                          {post.sharedPost.postDate ? (
                            <span className="rp-post__shared-time">
                              {new Date(post.sharedPost.postDate).toLocaleString([], {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </span>
                          ) : null}

                          <p>{post.sharedPost.caption}</p>

                          {sharedPostMediaSrc ? (
                            <img
                              src={sharedPostMediaSrc}
                              alt={t("feed_shared_media_alt")}
                              className="post-image rp-post__media"
                            />
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <TranslatablePostCaption
                        postId={post.id}
                        postType={post.postType}
                        text={post.caption}
                      />
                    )}
                  </div>
                ) : null}
              </div>

              {postChips.length > 0 ? (
                <div className="rp-post__chips" aria-label={t("post_tags", { defaultValue: "Post tags" })}>
                  {postChips.map((chip) => (
                    <span key={chip} className="rp-post__chip">
                      {chip}
                    </span>
                  ))}
                </div>
              ) : null}

              <div className={postActionsClassName}>
                <button
                  type="button"
                  className={[
                    "rp-post__action-btn",
                    currentUserHasLikedPost ? "rp-post__action-btn--liked" : "",
                    postLikePending ? "rp-post__action-btn--pending" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  title={t("like")}
                  aria-label={t("like")}
                  onClick={() =>
                    togglePostLike({
                      post,
                      actorUserId: user?.id,
                      errorMessage: t("post_like_update_failed", {
                        defaultValue: "Could not update the like. Please try again.",
                      }),
                      onSuccess: (data) => {
                        if (data?.liked) {
                          trackProductEvent("post_liked", {
                            featureArea: "feed",
                            metadata: getPostAnalyticsMetadata(post, "like_button"),
                          });
                        }
                      },
                    })
                  }
                  disabled={postLikePending}
                  aria-busy={postLikePending}
                >
                  <span className="rp-post__action-icon" aria-hidden="true">
                    <Heart
                      size={24}
                      strokeWidth={2.2}
                      fill={currentUserHasLikedPost ? "currentColor" : "none"}
                    />
                  </span>
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("like")} ({likeCount})
                  </span>
                  <span className="rp-post__action-count" aria-hidden="true">
                    {likeCount}
                  </span>
                </button>

                <button
                  type="button"
                  className="rp-post__action-btn"
                  title={t("comment")}
                  aria-label={t("comment")}
                  onClick={() => {
                    const next = openComments === post.id ? null : post.id;
                    setOpenComments(next);
                    setReplyTo(null);
                    setReplyText("");
                  }}
                >
                  <span className="rp-post__action-icon" aria-hidden="true">
                    <MessageCircle size={24} strokeWidth={2.2} />
                  </span>
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("comment")} ({commentCount})
                  </span>
                  <span className="rp-post__action-count" aria-hidden="true">
                    {commentCount}
                  </span>
                </button>

                <button
                  type="button"
                  className="rp-post__action-btn rp-post__action-btn--share"
                  title={t("share")}
                  aria-label={t("share")}
                  onClick={() => {
                    trackProductEvent("post_share_opened", {
                      featureArea: "feed",
                      metadata: getPostAnalyticsMetadata(post, "share_button"),
                    });
                    setSharePost(post);
                  }}
                >
                  <span className="rp-post__action-icon" aria-hidden="true">
                    <Forward size={24} strokeWidth={2.25} />
                  </span>
                  <span className="rp-post__action-copy rp-post__action-copy--desktop">
                    {t("share")} ({shareCount})
                  </span>
                </button>

                {post.postType !== "opinion" ? (
                  <button
                    type="button"
                    className="rp-post__action-btn rp-post__action-btn--info"
                    title={t("info")}
                    aria-label={t("info")}
                    onClick={() => handleOpenInfo(post)}
                  >
                    <FeedInfoActionIcon />
                    <span className="rp-post__action-copy rp-post__action-copy--desktop">
                      {t("info")}
                    </span>
                  </button>
                ) : null}
              </div>
              {openComments === post.id ? (
                <div className="post-comments rp-post__comments">
                  {(post.comments || []).map((comment) => {
                    const loadedUser = usersMap[comment.userId];

                    return (
                      <div key={comment.id} className="post-comment">
                        <div className="post-comment-header">
                          <img
                            src={
                              loadedUser?.avatar
                                ? `${API_BASE}${loadedUser.avatar}`
                                : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                            }
                            alt={t("profile_picture")}
                            className="post-comment-avatar"
                          />
                          <strong>{comment.username}</strong>
                        </div>

                        <div className="post-comment-text">{comment.text}</div>

                        <div className="post-comment-actions">
                          <button
                            className="comment-like-btn"
                            onClick={async () => {
                              const token = localStorage.getItem("token");

                              try {
                                const fd = new FormData();
                                if (token) {
                                  fd.append("token", token);
                                }
                                fd.append("post_id", post.id);
                                fd.append("comment_id", comment.id);

                                const res = await fetch(
                                  `${API_BASE}/posts/comment/like`,
                                  withApiCredentials({
                                    method: "POST",
                                    body: fd,
                                  })
                                );
                                if (!res.ok) throw new Error("comment like failed");

                                const data = await res.json();

                                window.dispatchEvent(
                                  new CustomEvent("commentLiked", {
                                    detail: {
                                      postId: post.id,
                                      commentId: comment.id,
                                      likes: data.likes,
                                    },
                                  })
                                );
                              } catch (err) {
                                console.error("Comment like failed:", err);
                              }
                            }}
                          >
                            {"\uD83D\uDC4D"} {t("like")} ({comment.likes?.length || 0})
                          </button>

                          <button
                            className="comment-reply-btn"
                            onClick={() =>
                              setReplyTo(replyTo === comment.id ? null : comment.id)
                            }
                          >
                            {"\uD83D\uDCAC"} {t("reply")}
                          </button>
                        </div>

                        {(comment.replies || []).map((reply) => (
                          <div key={reply.id} className="post-reply">
                            <div className="post-reply-text">
                              <strong>{reply.username}</strong> {reply.text}
                            </div>
                            <button
                              className="reply-like-btn"
                              onClick={async () => {
                                const token = localStorage.getItem("token");

                                try {
                                  const fd = new FormData();
                                  if (token) {
                                    fd.append("token", token);
                                  }
                                  fd.append("post_id", post.id);
                                  fd.append("comment_id", comment.id);
                                  fd.append("reply_id", reply.id);

                                  const res = await fetch(
                                    `${API_BASE}/posts/comment/reply/like`,
                                    withApiCredentials({
                                      method: "POST",
                                      body: fd,
                                    })
                                  );
                                  if (!res.ok) {
                                    throw new Error("reply like failed");
                                  }

                                  const data = await res.json();

                                  window.dispatchEvent(
                                    new CustomEvent("replyLiked", {
                                      detail: {
                                        postId: post.id,
                                        commentId: comment.id,
                                        replyId: reply.id,
                                        likes: data.likes,
                                      },
                                    })
                                  );
                                } catch (err) {
                                  console.error("Reply like failed:", err);
                                }
                              }}
                            >
                              {"\uD83D\uDC4D"} {t("like")} ({reply.likes?.length || 0})
                            </button>
                          </div>
                        ))}

                        {replyTo === comment.id ? (
                          <div className="post-reply-form">
                            <input
                              type="text"
                              value={replyText}
                              placeholder={t("write_reply")}
                              onChange={(event) => setReplyText(event.target.value)}
                            />
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem("token");
                                if (!replyText.trim()) return;

                                try {
                                  const fd = new FormData();
                                  if (token) {
                                    fd.append("token", token);
                                  }
                                  fd.append("post_id", post.id);
                                  fd.append("comment_id", comment.id);
                                  fd.append("text", replyText);

                                  const res = await fetch(
                                    `${API_BASE}/posts/comment/reply`,
                                    withApiCredentials({
                                      method: "POST",
                                      body: fd,
                                    })
                                  );
                                  if (!res.ok) throw new Error("reply failed");

                                  const data = await res.json();

                                  window.dispatchEvent(
                                    new CustomEvent("commentReplied", {
                                      detail: {
                                        postId: post.id,
                                        commentId: comment.id,
                                        reply: data.reply,
                                      },
                                    })
                                  );

                                  setReplyText("");
                                  setReplyTo(null);
                                } catch (err) {
                                  console.error("Reply failed:", err);
                                }
                              }}
                            >
                              {t("send")}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}

                  <div className="post-comment-form">
                    <input
                      type="text"
                      value={commentText}
                      placeholder={t("write_comment")}
                      onChange={(event) => setCommentText(event.target.value)}
                    />
                    <button
                      onClick={async () => {
                        const token = localStorage.getItem("token");
                        if (!commentText.trim()) return;

                        try {
                          const fd = new FormData();
                          if (token) {
                            fd.append("token", token);
                          }
                          fd.append("post_id", post.id);
                          fd.append("text", commentText);

                          const res = await fetch(
                            `${API_BASE}/posts/comment`,
                            withApiCredentials({
                              method: "POST",
                              body: fd,
                            })
                          );
                          if (!res.ok) throw new Error("comment failed");

                          const data = await res.json();
                          trackProductEvent("post_commented", {
                            featureArea: "feed",
                            metadata: getPostAnalyticsMetadata(post, "comment_form"),
                          });

                          window.dispatchEvent(
                            new CustomEvent("postCommented", {
                              detail: {
                                postId: post.id,
                                comment: data.comment,
                              },
                            })
                          );

                          setCommentText("");
                          setOpenComments(null);
                        } catch (err) {
                          console.error("Comment failed:", err);
                        }
                      }}
                    >
                      {t("send")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}

        <PaginationSentinel
          enabled={Boolean(user?.id) && hasMorePosts}
          loading={isLoadingMorePosts}
          onLoadMore={onLoadMorePosts}
          loadingLabel={t("loading", { defaultValue: "Loading…" })}
          testId="feed-pagination-sentinel"
        />

        <div className="rp-feed__spacer" aria-hidden="true"></div>

        {zoomImage ? (
          <div
            className="image-modal-overlay"
            onClick={() => setZoomImage(null)}
          >
            <button
              className="image-modal-close"
              onClick={(event) => {
                event.stopPropagation();
                setZoomImage(null);
              }}
            >
              {"\u2715"}
            </button>

            <img
              src={zoomImage}
              alt={t("feed_zoomed_media_alt")}
              className="image-modal-img"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        ) : null}

        {zoomVideo ? (
          <div
            className="image-modal-overlay image-modal-overlay--video"
            onClick={(event) => {
              if (event.target !== event.currentTarget) return;
              closeZoomVideo({ reason: "backdrop" });
            }}
          >
            <button
              className="image-modal-close"
              onClick={(event) => {
                event.stopPropagation();
                closeZoomVideo({ reason: "close_button" });
              }}
            >
              {"\u2715"}
            </button>

            <video
              key={`${zoomVideoInstanceId}:${zoomVideoUrl}`}
              ref={setZoomVideoNode}
              src={zoomVideoUrl}
              controls
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              playsInline
              autoPlay
              preload="auto"
              className="image-modal-video"
              onLoadStart={(event) => logZoomVideoEvent("loadstart", event)}
              onLoadedMetadata={handleZoomVideoReadyEvent}
              onCanPlay={handleZoomVideoReadyEvent}
              onPlaying={handleZoomVideoPlayingEvent}
              onPause={(event) => logZoomVideoEvent("pause", event)}
              onError={(event) => logZoomVideoEvent("error", event)}
              onStalled={(event) => logZoomVideoEvent("stalled", event)}
              onWaiting={(event) => logZoomVideoEvent("waiting", event)}
              onAbort={(event) => logZoomVideoEvent("abort", event)}
              onEnded={(event) => logZoomVideoEvent("ended", event)}
              onClick={(event) => event.stopPropagation()}
            />
            {zoomVideoPlayFailed ? (
              <button
                type="button"
                onClick={handleZoomVideoFallbackPlay}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  minWidth: "148px",
                  minHeight: "52px",
                  padding: "12px 18px",
                  border: "1px solid rgba(255, 255, 255, 0.58)",
                  borderRadius: "999px",
                  background: "rgba(0, 0, 0, 0.68)",
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: 800,
                  zIndex: 5002,
                }}
              >
                {t("tap_to_play", { defaultValue: "Tap to play" })}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
